import { useState } from 'react';
import { CalendarClock, Lock, Moon, PackagePlus, Sun, TrendingUp, TriangleAlert } from 'lucide-react';
import { useLogin } from '@/lib/hooks';
import { useTema } from '@/lib/tema';
import { cn } from '@/lib/cn';

const INPUT =
  'h-11 w-full rounded-lg border border-hairline bg-card px-3 text-sm text-ink-strong placeholder:text-ink-faint ' +
  'transition-[border-color,box-shadow] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-ring/35';

/** Lo que hace la herramienta, dicho sin cifras: en una pantalla de login cualquier número
 *  sería inventado, y este proyecto tiene por regla no mostrar datos que no salgan de la base. */
const QUE_HACE = [
  { icono: TrendingUp, texto: 'Cuánto se va a vender de cada producto, con el error medido a la vista' },
  { icono: CalendarClock, texto: 'Cuándo pedirlo, según lo que tarda cada proveedor en entregar' },
  { icono: PackagePlus, texto: 'Y cuánto comprar, sin quedarte corto durante la espera' },
];

/**
 * Login con las credenciales del B2B: no hay usuarios propios. Quien tiene cuenta en el
 * ecommerce entra acá con la misma, siempre que su rol sea interno (ver server/auth.ts).
 *
 * Es la primera pantalla del producto y era la de siempre: fondo liso, una tarjeta en el
 * medio y nada más. Ahora la mitad izquierda dice qué hace la herramienta —quien entra por
 * primera vez llega mandado por otro— y el fondo tiene una retícula técnica que la ata a la
 * estética del resto: esto es una herramienta de operaciones, no una app de consumo.
 */
export default function LoginPage() {
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { tema, alternar } = useTema();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    login.mutate({ email: email.trim(), password });
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-canvas">
      {/* Atmósfera: una retícula de plano técnico y dos halos del color de marca. Va en un
          `aria-hidden` porque no comunica nada — es textura, no información. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--c-hover) / 0.06) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgb(var(--c-hover) / 0.06) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 30% 40%, black, transparent)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 30% 40%, black, transparent)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 -top-40 h-[32rem] w-[32rem] rounded-full opacity-[0.18] blur-3xl"
        style={{ background: 'radial-gradient(circle, rgb(var(--c-primary)), transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-52 -right-40 h-[34rem] w-[34rem] rounded-full opacity-[0.14] blur-3xl"
        style={{ background: 'radial-gradient(circle, rgb(var(--c-info)), transparent 70%)' }}
      />

      <button
        onClick={alternar}
        aria-label={tema === 'claro' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
        title={tema === 'claro' ? 'Modo oscuro' : 'Modo claro'}
        className="absolute right-5 top-5 z-10 grid h-10 w-10 place-items-center rounded-lg text-ink-muted transition-[color,background-color] hover:bg-hover hover:text-ink-strong"
      >
        {tema === 'claro' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center px-5 py-12">
        <div className="grid w-full gap-12 lg:grid-cols-[1.1fr_minmax(0,26rem)] lg:gap-16">
          {/* --- Qué es esto ------------------------------------------------------- */}
          <div className="hidden flex-col justify-center lg:flex">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-to-br from-primary-ring to-info text-white shadow-glow">
                <TrendingUp className="h-6 w-6" strokeWidth={2.5} />
              </span>
              <div className="leading-tight">
                <p className="text-lg font-bold tracking-tight text-ink-strong">BetStock IA</p>
                <p className="text-2xs uppercase tracking-[0.14em] text-info-text">Forecast de demanda</p>
              </div>
            </div>

            <h1 className="mt-9 max-w-[16ch] text-[40px] font-bold leading-[1.05] tracking-[-0.03em] text-ink-strong">
              Comprar con el número, no con la memoria.
            </h1>

            <ul className="mt-8 max-w-prosa space-y-3.5">
              {QUE_HACE.map(({ icono: Icono, texto }, i) => (
                <li
                  key={texto}
                  className="flex items-start gap-3 text-sm leading-relaxed text-ink-muted animate-fade-in"
                  // Entrada escalonada: la lista se arma sola en vez de aparecer de golpe.
                  style={{ animationDelay: `${120 + i * 90}ms` }}
                >
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border border-hairline bg-card text-primary-text">
                    <Icono className="h-3.5 w-3.5" />
                  </span>
                  {texto}
                </li>
              ))}
            </ul>
          </div>

          {/* --- El formulario ------------------------------------------------------ */}
          <div className="mx-auto w-full max-w-sm lg:mx-0">
            {/* En pantalla angosta el panel de la izquierda no está: la marca va acá. */}
            <div className="flex items-center justify-center gap-2.5 lg:hidden">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-primary-ring to-info text-white shadow-glow">
                <TrendingUp className="h-5 w-5" strokeWidth={2.5} />
              </span>
              <div className="leading-tight">
                <p className="text-base font-bold text-ink-strong">BetStock IA</p>
                <p className="text-2xs uppercase tracking-[0.12em] text-info-text">Forecast de demanda</p>
              </div>
            </div>

            <div className="surface mt-7 p-6 shadow-alta lg:mt-0">
              <h2 className="text-base font-semibold text-ink-strong">Ingresar</h2>
              <p className="mt-1 text-xs leading-relaxed text-ink-muted">
                Usá la misma cuenta del portal B2B de BET Supplier.
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <label className="flex flex-col gap-1.5">
                  <span className="text-2xs uppercase tracking-wide text-ink-faint">Email</span>
                  <input
                    type="email"
                    autoComplete="username"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@betsupplier.co"
                    className={INPUT}
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-2xs uppercase tracking-wide text-ink-faint">Contraseña</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={INPUT}
                  />
                </label>

                {login.isError && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft/60 p-3"
                  >
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-danger-text" />
                    <p className="text-xs leading-relaxed text-danger-text">
                      {login.error instanceof Error ? login.error.message : 'No se pudo iniciar sesión.'}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={login.isPending || !email.trim() || !password}
                  className={cn(
                    'inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-semibold text-white',
                    'transition-[background-color,scale] active:scale-[0.96] hover:bg-primary-hover',
                    // Deshabilitado en gris y no en un azul aclarado: un primario al 50%
                    // sigue leyéndose como un botón listo para apretar.
                    'disabled:cursor-not-allowed disabled:bg-ink-ghost disabled:active:scale-100',
                  )}
                >
                  {login.isPending ? 'Verificando…' : 'Ingresar'}
                </button>
              </form>
            </div>

            <p className="mt-5 flex items-start gap-1.5 px-2 text-2xs leading-relaxed text-ink-faint">
              <Lock className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                Herramienta interna de compras. Las cuentas de clientes mayoristas del B2B no tienen
                acceso.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
